<template>
  <div>
    <detail
      v-if="loaded"
      :onManager="onManager"
      :data="detailsData"
      :hiddenKeys="['created_at', 'updated_at', 'description']"
      :show-desc="false"
      :base-info="baseInfo"
      :extra-info="extraInfo"
      :hidden-base-info="proccessDefinitionKey === 'apply-internal-resource'" />
    <loading-block
      v-else
      :layout="loadingLayout" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import WindowsMixin from '@/mixins/windows'
import serverConfMixin from '@System/views/workflow/mixins/serverConf'
import ProjectQuotaMixin from '@System/views/workflow/mixins/projectQuota'
import DomainQuotaMixin from '@System/views/workflow/mixins/domainQuota'
import PriceMixin from '@System/views/workflow/mixins/price'
import ShopCartMixin from '@System/views/workflow/mixins/shopCart'
import ProcessHistory from '@System/views/workflow/components/ProcessHistory'
import ProcessList from '@System/views/workflow/components/ProcessList'
import ServerConfigList from '@System/views/workflow/components/ServerConfig'
import DeleteServers from '@System/views/workflow/components/DeleteServers'
import ApplyInternalResourceInfo from '@System/views/workflow/components/ApplyInternalResource'
import ShopCarts from '@System/views/workflow/components/ShopCarts'
import {
  getTimeTableColumn,
} from '@/utils/common/tableColumn'
import { WORKFLOW_TYPES } from '@/constants/workflow'
import { HYPERVISORS_GROUP } from '@/constants'
import { getProcessDefinitionNameTableColumn } from '../../utils/columns'

export default {
  name: 'MeProcessDetail',
  mixins: [serverConfMixin, ProjectQuotaMixin, DomainQuotaMixin, PriceMixin, ShopCartMixin, WindowsMixin],
  props: {
    data: {
      type: Object,
      required: true,
    },
    onManager: {
      type: Function,
      required: true,
    },
    workflowColumns: Array,
  },
  data () {
    return {
      loaded: false,
      allPublic: HYPERVISORS_GROUP.public,
      configData: {},
      skuInfo: {},
      regionInfo: {},
      zoneInfo: {},
      detailsData: { ...this.data, domainQuota: {}, projectQuota: {} },
      processInstanceInfo: {},
      deleteServers: [],
      processList: [],
      extraInfo: [],
      loadingLayout: [
        [10],
        [8, 9],
        [2, 4, 7, 5],
        [13, 9],
        [4, 3, 8],
        [8, 6, 8],
        [13, 9],
      ],
      shopCarts: [],
      shopCartOrders: [],
      orderSetStatus: '',
      orderSetPollTimer: null,
    }
  },
  computed: {
    ...mapGetters(['isDomainMode', 'isAdminMode', 'userInfo']),
    variables () {
      return this.data.variables
    },
    local_variables () {
      return this.data.local_variables
    },
    proccessDefinitionKey () {
      return this.data.process_definition_key || this.data.process_instance.process_definition_key
    },
    /** 当前详情会拉取 resource_order_sets（购物车）的流程类型 */
    isResourceOrderSetWorkflow () {
      return [
        WORKFLOW_TYPES.EXECUTE_RESOURCE_ORDER_SET,
        WORKFLOW_TYPES.EXECUTE_POLICY_UPDATE,
        WORKFLOW_TYPES.EXECUTE_USER_JOIN,
        WORKFLOW_TYPES.EXECUTE_USER_UPDATE,
        WORKFLOW_TYPES.EXECUTE_USER_DELETE,
        WORKFLOW_TYPES.EXECUTE_ROLE_SET_POLICIES,
        WORKFLOW_TYPES.EXECUTE_ROLEPOLICY_BATCH_DELETE,
        WORKFLOW_TYPES.EXECUTE_POLICY_ENABLE,
        WORKFLOW_TYPES.EXECUTE_POLICY_DISABLE,
      ].includes(this.proccessDefinitionKey)
    },
    paramter () {
      if (this.variables.paramter) {
        return JSON.parse(this.variables.paramter || '{}')
      }
      return {}
    },
    baseInfo () {
      const ret = [
        {
          field: 'initiator_name',
          title: this.$t('common_371'),
          formatter: ({ cellValue, row }) => {
            return row.variables.initiator_displayname || row.process_instance?.start_user_displayname || row.variables.initiator_name || row.process_instance?.start_user_name
          },
        },
        getProcessDefinitionNameTableColumn(),
        getTimeTableColumn({ field: 'start_time', title: this.$t('common_374') }),
        getTimeTableColumn({ field: 'end_time', title: this.$t('common_387') }),
      ]
      if (this.proccessDefinitionKey === WORKFLOW_TYPES.APPLY_SERVER_STOP) {
        const serverParam = JSON.parse(this.data.variables.paramter || '{}')
        if (serverParam.stop_charging) {
          ret.push({
            field: 'stop_charging',
            title: this.$t('compute.shutdown_stop_paying'),
            formatter: ({ row }) => {
              return this.$t('common.true')
            },
          })
        }
        if (serverParam.is_force) {
          ret.push({
            field: 'is_force',
            title: this.$t('compute.force_shutdown'),
            formatter: ({ row }) => {
              return this.$t('common.true')
            },
          })
        }
      }
      if (this.proccessDefinitionKey === WORKFLOW_TYPES.APPLY_SERVER_START) {
        const serverParam = JSON.parse(this.data.variables.paramter || '{}')
        if (serverParam.auto_prepaid) {
          ret.push({
            field: 'auto_prepaid',
            title: this.$t('compute.change_to_prepaid'),
            formatter: ({ row }) => {
              return this.$t('common.true')
            },
          })
        }
      }
      return ret
    },
  },
  watch: {
    paramter: {
      handler: function (val) {
        if (WORKFLOW_TYPES.APPLY_DOMAIN_QUOTA === this.proccessDefinitionKey) {
          this.getDomainQuotaData(val.domain)
        }
        if (WORKFLOW_TYPES.APPLY_PROJECT_QUOTA === this.proccessDefinitionKey) {
          this.getProjectQuotaData(val.tenant)
        }
      },
      immediate: true,
    },
    'data.id' () {
      this.clearOrderSetPolling()
    },
  },
  async created () {
    const that = this
    switch (this.proccessDefinitionKey) {
      case WORKFLOW_TYPES.APPLY_MACHINE:
        await this.getApplyMachineData()
        await this.initResourceViewInfo()
        await this.initPriceInfo()
        break
      case WORKFLOW_TYPES.APPLY_SERVER_CHANGECONFIG:
        this.initServerConfigInfo()
        break
      case WORKFLOW_TYPES.APPLY_PROJECT_QUOTA:
        this.initProjectQuotaInfo(that.paramter)
        break
      case WORKFLOW_TYPES.APPLY_DOMAIN_QUOTA:
        this.initDomainQuotaInfo(that.paramter)
        // this.getDomainQuotaData(that.paramter.domain)
        break
      case WORKFLOW_TYPES.APPLY_JOIN_PROJECT:
        this.initProjectInfo()
        break
      case WORKFLOW_TYPES.APPLY_SERVER_DELETE:
        await this.getOperateServerData(that.variables.ids)
        await this.initOperateServerInfo()
        break
      case WORKFLOW_TYPES.APPLY_SERVER_RESTART:
        await this.getOperateServerData(that.variables.ids)
        await this.initOperateServerInfo()
        break
      case WORKFLOW_TYPES.APPLY_SERVER_STOP:
        await this.getOperateServerData(that.variables.ids)
        await this.initOperateServerInfo()
        break
      case WORKFLOW_TYPES.APPLY_INTERNAL_RESOURCE:
        await this.initApplyInternalResourceInfo(that.paramter)
        break
      case WORKFLOW_TYPES.EXECUTE_RESOURCE_ORDER_SET:
      case WORKFLOW_TYPES.EXECUTE_POLICY_UPDATE:
      case WORKFLOW_TYPES.EXECUTE_USER_JOIN:
      case WORKFLOW_TYPES.EXECUTE_USER_UPDATE:
      case WORKFLOW_TYPES.EXECUTE_USER_DELETE:
      case WORKFLOW_TYPES.EXECUTE_ROLE_SET_POLICIES:
      case WORKFLOW_TYPES.EXECUTE_ROLEPOLICY_BATCH_DELETE:
      case WORKFLOW_TYPES.EXECUTE_POLICY_ENABLE:
      case WORKFLOW_TYPES.EXECUTE_POLICY_DISABLE:
        await this.getShopCarts(this.variables.ids)
        await this.initShopCartsInfo()
        break
    }
    if (this.isDomainMode || this.isAdminMode) {
      if (WORKFLOW_TYPES.APPLY_PROJECT_QUOTA === this.proccessDefinitionKey) {
        this.getProjectDomainQuotaData(this.paramter.domain)
      }
    }
    this.initProcessInstanceInfo()
  },
  deactivated () {
    this.clearOrderSetPolling()
  },
  beforeDestroy () {
    this.clearOrderSetPolling()
  },
  methods: {
    clearOrderSetPolling () {
      if (this.orderSetPollTimer != null) {
        clearInterval(this.orderSetPollTimer)
        this.orderSetPollTimer = null
      }
    },
    syncOrderSetPolling () {
      this.clearOrderSetPolling()
      if (!this.isResourceOrderSetWorkflow || !this.variables?.ids) return
      if (this.orderSetStatus !== 'executing') return
      const id = this.variables.ids
      this.orderSetPollTimer = setInterval(() => {
        this.getShopCarts(id)
      }, 10000)
    },
    initServerConfigInfo () {
      const serverConfList = {
        title: this.$t('common_376'),
        field: 'serverConfList',
        slots: {
          default: ({ row }, h) => {
            return [
              <ServerConfigList variables={ this.variables } />,
            ]
          },
        },
      }
      this.extraInfo.push(serverConfList)
    },
    initProcessInstanceInfo () {
      this.getProcessInstance(this.data.process_instance_id || this.data.id)
        .then((res) => {
          const pInfo = res.data || {}
          this.getProcessInstanceData(pInfo)
          this.initProcessHistorys()
          this.initProcessList()
          this.loaded = true
        })
        .catch(() => {
          this.loaded = true
        })
    },
    getProcessInstanceData (processInstanceInfo) {
      if (this.proccessDefinitionKey !== WORKFLOW_TYPES.CUSTOMER_SERVICE) {
        processInstanceInfo.log.unshift({
          activity_name: this.$t('common_377'),
          task_assignee_name: processInstanceInfo.variables && (processInstanceInfo.variables.initiator_displayname || processInstanceInfo.variables.initiator_name),
          end_time: processInstanceInfo.start_time,
          task: {
            local_variables: {
              comment: processInstanceInfo.variables.description,
            },
            // comments: [{ full_message: processInstanceInfo.variables.description }]
          },
        })
      }
      this.processInstanceInfo = processInstanceInfo
      const processList = []
      processInstanceInfo.definition.forEach((item) => {
        if (!(item.activity_id || '').includes('Trans')) {
          processList.push({
            title: item.activity_name,
            activity_id: item.activity_id,
            assignees: item.activity_instance.map((v) => {
              return { id: v.assignee, name: v.task_assignee_displayname || v.task_assignee_name }
            }),
          })
          // 调整转单节点顺序
          const targets = processInstanceInfo.definition.filter(t => {
            return t.activity_id === `Trans${item.activity_id}`
          })
          if (targets[0]) {
            processList.push({
              title: targets[0].activity_name,
              activity_id: item.activity_id,
              assignees: targets[0].activity_instance.map((v) => {
                return { id: v.assignee, name: v.task_assignee_displayname || v.task_assignee_name }
              }),
            })
          }
        }
      })
      if (this.proccessDefinitionKey === WORKFLOW_TYPES.APPLY_MACHINE || this.proccessDefinitionKey === WORKFLOW_TYPES.APPLY_SERVER_CHANGECONFIG) {
        processList.push({
          title: this.$t('common_378'),
          assignees: [{ id: null, name: this.$t('common_379') }],
        })
      }
      this.processList = processList
    },
    initProcessHistorys () {
      const processHistory = {
        title: this.$t('common_380'),
        field: 'processHistory',
        slots: {
          default: ({ row }, h) => {
            return [
              <ProcessHistory processInstanceInfo={ this.processInstanceInfo } />,
            ]
          },
        },
      }
      this.extraInfo.push(processHistory)
    },
    initProcessList () {
      this.processList.unshift({
        title: this.$t('common_377'),
        assignees: this.processInstanceInfo.variables && (this.processInstanceInfo.variables.initiator_displayname || this.processInstanceInfo.variables.initiator_name),
      })
      const processList = {
        title: this.$t('common_381'),
        field: 'processList',
        slots: {
          default: ({ row }, h) => {
            return [
              <ProcessList processList={ this.processList } processInstanceInfo={this.processInstanceInfo} />,
            ]
          },
        },
      }
      this.extraInfo.push(processList)
    },
    initProjectInfo () {
      const variables = this.data.variables
      const projectInfo = [
        {
          title: this.$t('common_382', [this.$t('dictionary.project')]),
          items: [
            {
              field: 'description',
              title: this.$t('common_157'),
              formatter: ({ cellValue, row }) => {
                return variables.description || '-'
              },
            },
            {
              field: 'project',
              title: this.$t('common_384'),
              formatter: ({ cellValue, row }) => {
                return variables.joinProjectName || '-'
              },
            },
          ],
        },
      ]
      this.extraInfo.push(...projectInfo)
    },
    initOperateServerInfo () {
      const deleteServerList = {
        title: this.$t('common_385'),
        field: 'deleteServerList',
        slots: {
          default: ({ row }, h) => {
            return [
              <DeleteServers deleteServers={ this.deleteServers } />,
            ]
          },
        },
      }
      this.extraInfo.push(deleteServerList)
    },
    getOperateServerData (serverIds = []) {
      if (!Array.isArray(serverIds)) {
        serverIds = [serverIds]
      }
      const servers = JSON.parse(this.variables.parameter || '[]')
      if (servers.length > 0) {
        this.deleteServers = servers
        return
      }
      const params = { filter: `id.in(${serverIds.map(item => `'${item}'`).join(',')})` }
      new this.$Manager('servers', 'v2')
        .list({ params })
        .then((res) => {
          this.deleteServers = res.data.data
        })
        .catch(() => {
          this.deleteServers = []
        })
    },
    initApplyInternalResourceInfo () {
      const applyInternalResourceInfo = {
        title: this.$t('common.workflow_detail'),
        field: 'applyInternalResourceInfo',
        slots: {
          default: ({ row }, h) => {
            return [
              <ApplyInternalResourceInfo resource="historic-process-instances" variables={ this.variables } local_variables={ this.local_variables } data={ this.data } columns={ this.workflowColumns } />,
            ]
          },
        },
      }
      this.extraInfo.push(applyInternalResourceInfo)
    },
    async getShopCarts (orderSetResId) {
      try {
        const resOrderM = new this.$Manager('resource_order_sets')
        const orderRes = await resOrderM.get({ id: orderSetResId })
        this.shopCarts = orderRes.data.parameters || []
        this.shopCartOrders = orderRes.data.orders || []
        this.orderSetStatus = orderRes.data.status || ''
        this.syncOrderSetPolling()
      } catch (error) {
        console.log('fetch shop carts error')
      }
    },
    initShopCartsInfo () {
      const isApplyResourceList = this.proccessDefinitionKey === WORKFLOW_TYPES.EXECUTE_RESOURCE_ORDER_SET
      const titleText = isApplyResourceList ? this.$t('scope.shopcart.apply_resource_list') : this.$t('scope.shopcart.apply_info')
      this.extraInfo.push({
        title: ({ row }, h) => {
          return h('div', {
            class: 'detail-title',
            style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' },
          }, [
            h('div', { style: { display: 'flex', alignItems: 'center', minWidth: 0 } }, [
              h('icon', { props: { name: 'info2' } }),
              h('span', { class: 'ml-2' }, titleText),
            ]),
            isApplyResourceList
              ? h('a-tooltip', {
                props: {
                  title: this.$t('scope.shopcart.re_execute_tip'),
                },
              }, [
                h('span', { style: { display: 'inline-block' } }, [
                  h('a-button', {
                    props: {
                      type: 'link',
                      disabled: this.data.state !== 'ACTIVE' || this.orderSetStatus !== 'execute_failed',
                    },
                    on: {
                      click: () => this.handleReexecuteResourceOrderSet(),
                    },
                  }, this.$t('scope.shopcart.re_execute')),
                ]),
              ])
              : null,
          ].filter(Boolean))
        },
        field: 'shopCarts',
        slots: {
          default: ({ row }, h) => {
            return [
              <ShopCarts data={ this.shopCarts } workflowData={ this.data } orders={ this.shopCartOrders } orderSetStatus={ this.orderSetStatus } canUpdate={ this.data.state === 'ACTIVE' && !this.data.variables.audit_status } orderSetResId={ this.variables.ids } onRefresh={ this.handleUpdateShopCart } onDelete={ this.handleDeleteShopCart } onEdit={ this.handleEditShopCart } onView={ this.handleViewShopCart } />,
            ]
          },
        },
      })
    },
    async handleReexecuteResourceOrderSet () {
      if (this.orderSetStatus !== 'execute_failed') return
      try {
        await new this.$Manager('resource_order_sets', 'v1').performAction({
          id: this.variables.ids,
          action: 'execute',
          data: {
            force: true,
          },
        })
        await this.getShopCarts(this.variables.ids)
        this.$message.success(this.$t('common.success'))
      } catch (e) {
        this.$message.error(this.$t('common.failed'))
      }
    },
    async handleDeleteShopCart (data, rets) {
      const resOrderM = new this.$Manager('resource_order_sets')
      const orderRes = await resOrderM.get({ id: this.variables.ids })
      if (orderRes.data && orderRes.data.parameters) {
        const parameters = orderRes.data.parameters.filter((_, idx) => idx !== rets.rowIndex)
        await resOrderM.update({ id: this.variables.ids, data: { parameters } })
        await this.getShopCarts(this.variables.ids)
        this.$message.success(this.$t('common.success'))
      }
    },
    handleEditShopCart (data, rets) {
      const serverData = { ...data.parameter, __count__: data.count }
      if (data.resource !== 'servers' || data.action !== 'create') return
      const resourceType = serverData.extraData?.__resource_type__
      const routeNameMap = {
        baremetal: 'BaremetalCreate',
        server_container: 'VMContainerInstanceCreate',
        server: 'VMInstanceCreate',
      }
      this.$router.push({
        name: routeNameMap[resourceType] || 'VMInstanceCreate',
        query: {
          type: serverData.extraData?.formType,
          workflow: this.data.id,
          order_set_id: this.variables.ids,
          order_set_idx: rets.rowIndex,
        },
        params: {
          data: serverData,
        },
      })
    },
    async handleUpdateShopCart () {
      await this.getShopCarts(this.variables.ids)
      await this.initShopCartsInfo()
    },
  },
}
</script>
